/**
 * ScoreVarianceHelpDrawer — RC 1.47.5 post-Phase-3.
 *
 * "What is ±5?" contextual help reachable from the dashboard top-bar.
 * Operator-approved expansion of the same TOOLTIP_COPY constant into
 * a longer-form, premium, institution-grade explanation surface.
 *
 * Tone: concise, premium, institutional. Not marketing copy.
 *
 * Read-only. No charge. No mutation. Suppressed when the
 * canonical-fit feature flag is hard-disabled (the operator's
 * emergency kill switch).
 */

import { Info } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { TOOLTIP_COPY } from '@/lib/scoreVarianceCopy';
import { isCanonicalFitUiEnabled } from '@/lib/rc_1_47_5_flag';

export default function ScoreVarianceHelpDrawer() {
  if (!isCanonicalFitUiEnabled()) return null;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label="What is \u00b15?"
          data-testid="score-variance-help-trigger"
          className="
            inline-flex items-center gap-1.5 px-2 py-1 text-xs
            text-ndor-ink-secondary hover:text-ndor-ink hover:bg-ndor-surface-alt
            rounded-md
          "
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden md:inline">What is &plusmn;5?</span>
          <span className="md:hidden font-mono-ui">&plusmn;5</span>
        </Button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="score-variance-help-drawer"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-lg font-medium">
            What does &ldquo;&plusmn;5&rdquo; mean on NDORFlow scores?
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-5 text-sm leading-relaxed text-ndor-ink">
          {/* Headline definition — the constant verbatim */}
          <section className="rounded-md border border-ndor-edge bg-muted/30 p-4">
            <p className="text-ndor-ink">{TOOLTIP_COPY}</p>
          </section>

          {/* Directional scoring */}
          <section className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-ndor-ink-secondary">
              Directional, not arithmetic
            </h3>
            <p className="text-ndor-ink-muted">
              Every NDOR score &mdash; Overall Fit, NDOR CV verdict, sub-scores &mdash; is a
              <em> directional assessment</em> produced by independent grader passes against
              a fixed rubric. It is not a measurement on a calibrated instrument. The same
              candidate, CV and job description can score 76 on one run and 78 on another
              without anything materially changing about the candidate&apos;s fit.
            </p>
          </section>

          {/* Why ±5 */}
          <section className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-ndor-ink-secondary">
              Why &plusmn;5?
            </h3>
            <p className="text-ndor-ink-muted">
              From observed run-to-run dispersion across thousands of grading passes, NDOR
              scores typically fluctuate within a five-point band even when the underlying
              evidence is unchanged. A drift of 1&ndash;5 points in either direction is
              normal noise, not a signal.
            </p>
          </section>

          {/* Interpretation guide */}
          <section className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-ndor-ink-secondary">
              How to read score changes
            </h3>
            <ul className="space-y-1.5 text-ndor-ink-muted">
              <li>
                <span className="font-medium text-ndor-ink">|&Delta;|&nbsp;&le;&nbsp;5</span>
                &nbsp;&mdash; treat as steady-state. The candidate&apos;s position has not
                materially changed.
              </li>
              <li>
                <span className="font-medium text-ndor-ink">|&Delta;|&nbsp;6&ndash;9</span>
                &nbsp;&mdash; meaningful directional movement. Worth investigating but not
                dramatic.
              </li>
              <li>
                <span className="font-medium text-ndor-ink">|&Delta;|&nbsp;&ge;&nbsp;10</span>
                &nbsp;&mdash; strong signal. Examine the strongest evidence shifts in the
                reasoning panel.
              </li>
            </ul>
          </section>

          {/* RC 1.47.1 governance link */}
          <section className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-ndor-ink-secondary">
              How NDORFlow uses this internally
            </h3>
            <p className="text-ndor-ink-muted">
              The platform&apos;s tolerance governance treats the same band as material:
              when a Tailored Variant&apos;s NDOR CV verdict moves within &plusmn;5 of its
              Source baseline, the post-generation charge is automatically waived as
              <em> &ldquo;within NDOR confidence band&rdquo;</em>. Material improvement is
              recognised only when the verdict moves at least six points above the baseline.
              Refunds, free-rebuild rules and pack canonical fit all use the same band so
              user-visible numbers and operator-side governance stay aligned.
            </p>
          </section>

          {/* Closing — institution-grade footer */}
          <section className="rounded-md border border-ndor-edge bg-muted/20 p-4">
            <p className="text-xs text-ndor-ink-muted">
              NDORFlow surfaces the &plusmn;5 band on every fit number and verdict so you
              never have to guess whether a small movement is meaningful. The score itself
              remains canonical and unchanged &mdash; the band is interpretive framing,
              not a recalculation.
            </p>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
